/**
 * Authentication Type Definitions
 * 
 * Types for authentication state, user data, and auth context.
 */

/**
 * User roles
 */
export enum UserRole {
  SUPER_ADMIN = 'SUPER_ADMIN',
  ADMIN = 'ADMIN',
  USER = 'USER',
}

/**
 * Feature type representing a feature in the organization
 */
export interface Feature {
  id: string;
  name: string;
  enabled: boolean;
}

/**
 * Organization type matching the API response structure
 */
export interface Organization {
  id: string;
  name: string;
  slug: string;
  domain: string;
  email_domain: string;
  schema_name: string;
  sso_enabled: boolean;
  sso_provider: string | null;
  sso_config: unknown | null;
  status: string;
  subscription_tier: string | null;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
  features?: Feature[];
}

/**
 * User type representing an authenticated user
 */
export interface User {
  userId?: number | string;
  username: string;
  firstName?: string;
  lastName?: string;
  role: UserRole;
  redirectUrl?: string;
  clients?: any[];
  groups?: any[];
  isLoggedIn: boolean;
  email?: string;
  organization_id?: string;
  tenant_id?: string;
  organization?: Organization;
}


export interface ValidateTokenResponse extends User {
  valid: boolean;
}
/**
 * Login credentials
 */
export interface LoginCredentials {
  email: string;
  password: string;
}

/**
 * OTP validation payload
 */
export interface OTPValidation {
  email: string;
  otp_code: string;
}

/**
 * Token validation payload
 */
export interface TokenValidation {
  token: string;
}

/**
 * Auth response from OTP validation
 */
export interface AuthResponse {
  message: string;
  username: string;
  email: string;
  role: string;
  access_token: string;
  refresh_token: string;
  token_type: string;
  expires_in: number;
  user: {
    id: string;
    email: string;
    first_name: string;
    last_name: string;
    status: string;
    roles: string[];
  };
  tenant: {
    id: string;
    domain: string;
  };
  organization?: Organization;
}

/**
 * OTP response from login
 */
export interface OTPResponse {
  message: string;
  role?: UserRole;
  redirect_url?: string;
}

/**
 * Login response (initial login before OTP)
 */
export interface LoginResponse {
  success: boolean;
  message: string;
  user?: User;
}

/**
 * JWT payload structure
 */
export interface JWTPayload {
  exp: number;
  iat: number;
  sub: string;
  username: string;
  role: UserRole;
}

/**
 * Auth state
 */
export interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
}

/**
 * Update user data payload
 */
export interface UpdateUserData {
  firstName?: string;
  lastName?: string;
  email?: string;
}

/**
 * Auth context type
 */
export interface AuthContextType extends AuthState {
  login: (user: AuthResponse, token: string) => void;
  logout: () => void;
  validateToken: () => Promise<boolean>;
  hasRole: (roles: UserRole | UserRole[]) => boolean;
  organization: Organization | null;
  hasFeature: (featureId: string) => boolean;
  updateUser: (data: UpdateUserData) => void;
}

/**
 * Reset password data
 */
export interface ResetPasswordData {
  token: string;
  email: string;
  new_password: string;
  confirm_password: string;
}

/**
 * Complete registration data
 */
export interface CompleteRegistrationData {
  token: string;
  temp_password: string;
  password: string;
  confirm_password: string;
}

/**
 * Auth service interface
 */
export interface AuthServiceInterface {
  login: (credentials: LoginCredentials) => Promise<OTPResponse>;
  validateOTP: (otpData: OTPValidation) => Promise<AuthResponse>;
  validateToken: (token: string) => Promise<ValidateTokenResponse>;
  getCurrentUser: () => Promise<User | null>;
  logout: () => Promise<void>;
  isAuthenticated: () => boolean;
  getStoredToken: () => string | null;
  getStoredUser: () => User | null;
  setToken: (token: string) => void;
  clearToken: () => void;
  forgotPassword: (email: string) => Promise<void>;
  resetPasswordWithToken: (data: ResetPasswordData) => Promise<AuthResponse>;
  completeRegistration: (data: CompleteRegistrationData) => Promise<AuthResponse>;
}
